import type { MessageDto, ReplyView } from '../api/client.ts';
import { formatAlbumLabel, formatCall } from './format.ts';

/**
 * The quote block a reply carries, worked out on this device.
 *
 * The server sends one down with every message (replyViewOf in
 * server/src/chats.ts) and that is the copy the thread ends up rendering. This
 * exists for the moment before that: an optimistic bubble appears the instant
 * the send button is pressed, and it has to appear complete — quote and all —
 * rather than growing one when the POST comes back. So the rules are mirrored
 * here, the same way summariseForList mirrors previewOf.
 */

/** Mirrors QUOTE_MAX in server/src/chats.ts. */
export const QUOTE_MAX = 140;

/**
 * What a quote of a message deleted for everyone says. Mirrors the wording
 * replyViewOf writes on the server, which is where a reloaded thread gets it;
 * this copy is for the deletion that happens while the thread is open.
 */
export const DELETED_QUOTE = 'Удалённое сообщение';

/**
 * One line of a message, in the words the chat list and the server both use for
 * it. Only text has anything to quote; every other kind is named by what it is.
 */
export function messagePreview(message: MessageDto): string {
  if (message.kind === 'text') return message.body ?? '';
  if (message.kind === 'image') return 'Изображение';
  // A recording's file name is a UUID nobody wants to read.
  if (message.kind === 'videoNote') return 'Кружочек';
  if (message.kind === 'voice') return 'Голосовое сообщение';
  if (message.kind === 'call') return formatCall(message);
  if (message.kind === 'album') {
    // Its caption if there is one, and what the group holds if there is not —
    // one line, and the caption is the more particular of the two. Mirrors
    // albumPreview on the server.
    const caption = message.body?.trim();
    if (caption) return caption;
    const videos = message.attachments.filter((item) => item.kind === 'video').length;
    return formatAlbumLabel(message.attachments.length - videos, videos);
  }
  return message.fileName ?? 'Файл';
}

/**
 * Trims a body down to the one line a quote shows, cutting back off any half a
 * smiley code the slice landed inside — which would otherwise render as the
 * literal `:wi` where a picture belongs. Mirrors quoteOf on the server.
 */
export function quoteOf(body: string): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  if (flat.length <= QUOTE_MAX) return flat;
  return `${flat.slice(0, QUOTE_MAX).replace(/:[a-zA-Z0-9_-]*$/, '').trimEnd()}…`;
}

/**
 * Whether the fragment a reply was written against is still in the message it
 * was written against. Both sides flattened, because that is the shape a quote
 * is stored and shown in — a fragment spanning a line break is one line by the
 * time anybody reads it back.
 */
export function quoteStillIn(quote: string, body: string): boolean {
  const needle = quote.replace(/\s+/g, ' ').trim();
  return needle !== '' && body.replace(/\s+/g, ' ').includes(needle);
}

/**
 * The line a reply's quote block shows.
 *
 * A reply against a selected fragment shows the fragment; a reply against the
 * whole message shows the start of it. Worked out at read time rather than
 * frozen when the reply was sent, which is what makes an edit of the original
 * carry: if the words somebody answered are still in there they go on being the
 * quote, and if the edit took them out the quote falls back to the message as it
 * reads now instead of showing a sentence that is no longer in it. Reverting the
 * edit brings the fragment back for the same reason.
 *
 * Mirrored by quotePreview in server/src/chats.ts, which is where a reloaded
 * thread gets it.
 */
export function quotePreview(body: string, quote: string | null): string {
  return quote !== null && quoteStillIn(quote, body) ? quoteOf(quote) : quoteOf(body);
}

/** The quote a reply to this message will carry, as the server will send it back. */
export function quoteMessage(message: MessageDto, quote: string | null = null): ReplyView {
  const text = message.kind === 'text';
  return {
    id: message.id,
    senderName: message.senderName,
    kind: message.kind,
    preview: text ? quotePreview(message.body ?? '', quote) : messagePreview(message),
    // Only a text message has a fragment to have answered: everything else is
    // named by what it is, and half a name is not a quote.
    quote: text ? quote : null,
    deleted: false,
  };
}
